import React, { useEffect, useState } from 'react';
import { employeeWalletAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Wallet, Send, ArrowUpRight, ArrowDownLeft, Loader2, Gift,
  UtensilsCrossed, Bus, Heart, Shield, Download, AlertCircle
} from 'lucide-react';

const fmt = (n) => `₦${Math.round(Number(n || 0)).toLocaleString()}`;
const CATEGORY_ICONS = { food: UtensilsCrossed, transport: Bus, health: Heart, insurance: Shield };
const CATEGORY_COLORS = { food: '#f59e0b', transport: '#ec4899', health: '#00B894', insurance: '#8b5cf6' };

export default function EmployeeWallet() {
  const [wallet, setWallet] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [txns, setTxns] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [w, b, t] = await Promise.all([
          employeeWalletAPI.getBalance(),
          employeeWalletAPI.getBenefitBalances(),
          employeeWalletAPI.getTransactions(),
        ]);
        setWallet(w); setBenefits(b); setTxns(t);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  const filters = ['all', 'salary', 'transfer', 'food', 'transport', 'health', 'insurance'];
  const filteredTxns = filter === 'all' ? txns : txns.filter(t => {
    if (filter === 'salary') return t.type?.includes('credit');
    if (filter === 'transfer') return t.type?.includes('transfer');
    return t.category === filter;
  });

  return (
    <div className="space-y-5 pb-6" data-testid="employee-wallet">
      <h1 className="text-xl font-bold text-gray-900">My Wallet</h1>

      {/* Balance Card */}
      <Card className="bg-gradient-to-br from-gray-900 to-gray-800 border-0 text-white">
        <CardContent className="p-5">
          <p className="text-xs text-gray-400 font-medium mb-1">Available Balance</p>
          <p className="text-3xl font-bold mb-4" style={{ fontFamily: 'JetBrains Mono' }}>{fmt(wallet?.balance)}</p>
          <div className="flex gap-2">
            <Button size="sm" className="bg-pink-500 hover:bg-pink-600 text-white text-xs h-8" onClick={() => navigate('/app/employee/wallet/send')}>
              <Send className="w-3 h-3 mr-1.5" /> Send Money
            </Button>
            <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 text-xs h-8">
              <Download className="w-3 h-3 mr-1.5" /> Request Payout
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Benefit Balances */}
      <div data-testid="benefit-balances">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Benefit Balances (this month)</h2>
        <div className="grid grid-cols-2 gap-2">
          {benefits.map(b => {
            const Icon = CATEGORY_ICONS[b.category] || Gift;
            const pct = b.allocated > 0 ? (b.used / b.allocated) * 100 : 0;
            return (
              <Card key={b.category} className="border-gray-200">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${CATEGORY_COLORS[b.category]}20` }}>
                      <Icon className="w-3 h-3" style={{ color: CATEGORY_COLORS[b.category] }} />
                    </div>
                    <span className="text-xs font-medium text-gray-900 capitalize">{b.category}</span>
                  </div>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-sm font-bold text-gray-900" style={{ fontFamily: 'JetBrains Mono' }}>{fmt(b.remaining)}</span>
                    <span className="text-[10px] text-gray-400">/ {fmt(b.allocated)}</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: CATEGORY_COLORS[b.category] }} />
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">{b.is_auto ? 'Auto-deducted' : `${Math.round(pct)}% used`}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Transaction History */}
      <div data-testid="transaction-history">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Recent Transactions</h2>
        <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1">
          {filters.map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`text-[11px] px-2.5 py-1 rounded-full font-medium whitespace-nowrap transition-colors ${filter === f ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <Card className="border-gray-200">
          <CardContent className="p-0 divide-y divide-gray-100">
            {filteredTxns.length === 0 && <p className="p-4 text-xs text-gray-400 text-center">No transactions found</p>}
            {filteredTxns.map((tx, i) => {
              const isCredit = tx.direction === 'credit' || tx.type?.includes('credit') || (tx.destination_wallet_id && !tx.source_wallet_id);
              const isFailed = tx.status === 'failed';
              const cat = tx.category || '';
              const CatIcon = isFailed ? AlertCircle : (CATEGORY_ICONS[cat] || (isCredit ? ArrowDownLeft : ArrowUpRight));
              return (
                <div key={i} className={`flex items-center justify-between px-4 py-3 ${isFailed ? 'opacity-60' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      isFailed ? 'bg-gray-100 text-gray-400' : isCredit ? 'bg-emerald-50 text-emerald-600' : 'bg-pink-50 text-pink-600'
                    }`}>
                      <CatIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className={`text-xs font-medium ${isFailed ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{tx.description || tx.narration || 'Transaction'}</p>
                      <p className="text-[10px] text-gray-400">
                        {tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }) : ''}
                        {tx.category ? ` \u2022 ${tx.category}` : ''}
                        {tx.reference ? ` \u2022 ${tx.reference}` : ''}
                        {isFailed && <span className="ml-1 text-red-400 font-semibold">Failed</span>}
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs font-bold ${
                    isFailed ? 'text-gray-400' : isCredit ? 'text-emerald-600' : 'text-red-600'
                  }`} style={{ fontFamily: 'JetBrains Mono' }}>
                    {isCredit ? '+' : '-'}{fmt(tx.amount)}
                  </span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
